import { useDisclosure } from "@chakra-ui/react";
import {
  BottomNav,
  NavLinkItem,
  NavbarExtraProps,
  Navbar as UINav,
} from "@picovico/ui";
import { getLocalStorageData } from "@picovico/utils";
import useLogout from "hooks/useLogout";
import usePwa from "hooks/usePwa";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "store";
import { updateCinemaHall } from "store/slices";
import { WebConfig } from "types/index";
import { withoutDisabledFeatureLinks } from "utils/featureLinks";
import IosInstallDialog from "./IosInstallDialog";
import { SearchMovies } from "./Search";

function Navbar({
  title,
  logo,
  navLinkItems,
  movieCardProps,
  displaySearchBar,
  ...props
}: {
  title: string;
  logo: string;
  navLinkItems: NavLinkItem[];
  movieCardProps: any;
  displaySearchBar: boolean;
} & NavbarExtraProps) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const config: WebConfig = useAppSelector((state) => state.config);
  const { cinema_hall } = useAppSelector((state) => state.activeCinemaHall);
  const activeCinemaHall = useMemo(
    () => cinema_hall || getLocalStorageData("cinema_hall"),
    [cinema_hall],
  );

  const activeCinemaHallName = useMemo(
    () =>
      config.cinema_halls.find((hall) => hall.idx === activeCinemaHall)?.name ||
      "",
    [config.cinema_halls, activeCinemaHall],
  );
  const { user, isLoggedIn } = useAppSelector((state) => state.user);
  const { isStandalone, isIOS, canInstall, promptInstall, canGoBack, goBack } =
    usePwa();
  const iosInstall = useDisclosure();
  const { logout } = useLogout();

  // iOS Safari has no `beforeinstallprompt`; offer a manual "Add to Home Screen"
  // dialog instead, but only when the tenant has a PWA and the app isn't already
  // installed (standalone).
  const showIosInstall = isIOS && !isStandalone && !!config.pwa_config;

  const currentPath = useMemo(
    () =>
      navLinkItems?.find((item) => item.href === router.pathname)?.label || "",
    [router, navLinkItems],
  );

  const mobileConfig = config.layout_config?.mobile_config;
  const isBottomNav = mobileConfig?.nav_style === "bottom_nav";
  // Logo placement: honor explicit config, else center while installed (PWA).
  const logoPosition =
    mobileConfig?.logo_position ?? (isStandalone ? "center" : "left");

  // Mobile (app-bar) logo size: override the base maxH from mobile_config.logo_size
  // while preserving any larger-breakpoint sizes the tenant set in logoProps.
  const cfgLogoMaxH = (props.logoProps as { maxH?: unknown } | undefined)?.maxH;
  const cfgLogoMaxHObj =
    cfgLogoMaxH && typeof cfgLogoMaxH === "object"
      ? (cfgLogoMaxH as Record<string, unknown>)
      : cfgLogoMaxH
        ? { base: cfgLogoMaxH }
        : { base: "1.75rem", xl: "4rem" };
  const mobileLogoMaxH = mobileConfig?.logo_size
    ? { ...cfgLogoMaxHObj, base: mobileConfig.logo_size }
    : undefined;
  // `navLinkItems` arrive already filtered (useWebConfig); the mobile lists and
  // the footer-derived "More" links come straight from the config, so entries
  // for a feature the company has off are dropped here.
  const features = config.features;
  const bottomNavItems = withoutDisabledFeatureLinks(
    features,
    mobileConfig?.bottom_nav_items?.length
      ? mobileConfig.bottom_nav_items
      : navLinkItems,
  );

  // The footer is hidden on mobile when the bottom nav is active, so its
  // links move into the "More" sheet. Prefer an explicit curated list, else
  // derive from the configured footer sections.
  const moreItems: NavLinkItem[] = useMemo(() => {
    if (mobileConfig?.more_items?.length) {
      return withoutDisabledFeatureLinks(features, mobileConfig.more_items);
    }
    const footerSections =
      config.template?.footer_components?.flatMap(
        (component) => component.props?.footerSections ?? [],
      ) ?? [];
    return withoutDisabledFeatureLinks(
      features,
      footerSections.flatMap(
        (section: { links?: { href?: string; label?: string }[] }) =>
          (section.links ?? [])
            .filter((link) => link.href && link.label)
            .map((link) => ({
              href: link.href,
              label: link.label as string,
            })),
      ),
    );
  }, [mobileConfig?.more_items, config.template?.footer_components, features]);

  const routeChangeFn = (href: string, target: string = "") => {
    if (window && target) {
      window.open(href, target);
    } else {
      router.push(href);
    }
  };

  return (
    <>
      <UINav
        title={title}
        isLoggedIn={isLoggedIn}
        loggedInUser={{
          name: user.name || null,
          email: user.email || null,
        }}
        search={displaySearchBar ? <SearchMovies {...movieCardProps} /> : <></>}
        displayCinemaHallButton={config.cinema_halls.length > 1}
        activeCinemaHall={activeCinemaHallName}
        onCinemaHallChange={() => {
          dispatch(updateCinemaHall({ isOpen: true }));
        }}
        logo={config.logo || logo}
        navLinkItems={navLinkItems}
        currentPath={currentPath}
        {...props}
        // When the bottom nav is active, give the top bar a native app-bar feel
        // on mobile (sticky, safe-area aware, subtle elevation) and hide the
        // header hamburger.
        {...(isBottomNav && {
          hamburgerWrapperProps: { display: "none" },
          // Defaults first so config-supplied navContainerWrapperProps
          // (e.g. a solid bg / custom shadow) can override them on mobile.
          navContainerWrapperProps: {
            position: { base: "sticky", xl: "static" },
            top: 0,
            zIndex: 1000,
            bg: "background.nav",
            boxShadow: { base: "0 1px 8px rgba(0,0,0,0.07)", xl: "none" },
            ...props.navContainerWrapperProps,
            sx: {
              paddingTop: "env(safe-area-inset-top)",
              ...props.navContainerWrapperProps?.sx,
            },
          },
          // Compact, vertically-centered app bar on mobile.
          navContainerProps: {
            minH: { base: "4rem", xl: "auto" },
            alignItems: "center",
            py: { base: 1.5, xl: undefined },
            ...props.navContainerProps,
          },
          // Slightly smaller brand mark so the bar reads as a mobile app bar.
          // mobile_config.logo_size overrides the base maxH (preserving md/xl).
          logoProps: {
            maxH: { base: "1.75rem", xl: "4rem" },
            ...props.logoProps,
            ...(mobileLogoMaxH && { maxH: mobileLogoMaxH }),
          },
          // Right-align the cinema-hall chip on mobile so it sits opposite the
          // logo (instead of floating in the dead center next to an empty
          // action cluster). Untouched on desktop.
          cinemaHallWrapperProps: {
            ml: { base: "auto", xl: 0 },
            ...props.cinemaHallWrapperProps,
          },
          // Compact cinema-hall chip.
          cinemaHallSelectorButtonProps: {
            size: "sm",
            ...props.cinemaHallSelectorButtonProps,
          },
        })}
        // PWA chrome: an Install button when the browser offers the prompt, a
        // back button when running standalone with in-app history, and a
        // configurable (PWA-default-centered) logo. These always win over
        // tenant config props above.
        logoPosition={logoPosition}
        showInstallButton={
          (canInstall || showIosInstall) && !!config.pwa_config
        }
        onInstall={canInstall ? promptInstall : iosInstall.onOpen}
        showBackButton={isStandalone && canGoBack && router.pathname !== "/"}
        onBack={goBack}
        onLogout={logout}
        loginPageRoute="/login"
        registerPageRoute="/register"
        routeChangeFn={routeChangeFn}
      />
      {isBottomNav && (
        <BottomNav
          navLinkItems={bottomNavItems}
          activeHref={router.pathname}
          isLoggedIn={isLoggedIn}
          showLabels={mobileConfig?.show_labels !== false}
          showMoreTab={mobileConfig?.show_more_tab !== false}
          moreLabel={mobileConfig?.more_label}
          moreItems={moreItems}
          tintImageIcons={mobileConfig?.tint_icons}
          moreSheetTitle={
            mobileConfig?.more_sheet_title ?? config.company_info?.name
          }
          {...mobileConfig?.bottom_nav_props}
          showRegister={
            (props as { showRegisterButton?: boolean }).showRegisterButton
          }
          onLogin={() => routeChangeFn("/login")}
          onRegister={() => routeChangeFn("/register")}
          onLogout={logout}
          routeChangeFn={routeChangeFn}
        />
      )}
      <IosInstallDialog
        isOpen={iosInstall.isOpen}
        onClose={iosInstall.onClose}
      />
    </>
  );
}

export default Navbar;
